import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { getAllResultBreakdowns, type ResultBreakdown } from "../../../service/result-breakdown"
import {
  listReports,
  generateReports,
  getReportPdfUrl,
  emailReport,
  getEmailLog,
  type ReportRecord,
  type EmailLogRow,
} from "../../../service/report-generation"

// Order breakdowns by title, numeric-aware so "Grade 2" precedes "Grade 10".
const sortBreakdowns = (list: ResultBreakdown[]) =>
  [...list].sort((a: any, b: any) =>
    String(a.title || a.name || "").localeCompare(String(b.title || b.name || ""), undefined, { numeric: true })
  )

const Reports = () => {
  const [breakdowns, setBreakdowns] = useState<ResultBreakdown[]>([])
  const [breakdownId, setBreakdownId] = useState("")
  const [records, setRecords] = useState<ReportRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [showLog, setShowLog] = useState(false)
  const [log, setLog] = useState<EmailLogRow[]>([])

  useEffect(() => {
    getAllResultBreakdowns({ limit: 300 })
      .then((r: any) => setBreakdowns(sortBreakdowns(r?.data?.data || r?.data || [])))
      .catch(() => setBreakdowns([]))
  }, [])

  const load = async (bId: string) => {
    if (!bId) {
      setRecords([])
      return
    }
    setLoading(true)
    try {
      const r = await listReports({ breakdownId: bId })
      setRecords(r?.items || [])
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load report cards")
      setRecords([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(breakdownId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdownId])

  const generateAll = async () => {
    if (!breakdownId) return
    setGenerating(true)
    try {
      const r = await generateReports({ breakdownId })
      toast.success(`Generated ${r.count} report card(s)${r.skipped_empty ? ` — skipped ${r.skipped_empty} with no marks` : ""}`)
      load(breakdownId)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Generation failed")
    } finally {
      setGenerating(false)
    }
  }

  const download = async (id: string) => {
    try {
      const r = await getReportPdfUrl(id)
      if (r?.url) window.open(r.url, "_blank")
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not open PDF")
    }
  }

  const doEmail = async (id: string) => {
    setBusyId(id)
    try {
      const r = await emailReport(id)
      const st = r?.data?.status
      if (st === "sent") toast.success("Email sent")
      else if (st === "skipped") toast.info("Email sending is disabled — logged, not sent")
      else toast.error(r?.data?.errorMessage || "Email not sent")
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Email failed")
    } finally {
      setBusyId(null)
    }
  }

  const openLog = async () => {
    try {
      const r = await getEmailLog()
      setLog(r?.items || [])
      setShowLog(true)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load email log")
    }
  }

  const statusBadge = (s: string) => {
    const cls =
      s === "sent" ? "bg-success" : s === "failed" ? "bg-danger" : s === "skipped" ? "bg-secondary" : "bg-warning text-dark"
    return <span className={`badge ${cls}`}>{s}</span>
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Reports</h3>
            <p className="text-muted mb-0">
              Report card PDFs are generated per (student, breakdown). Emails go to the parent on file.
            </p>
          </div>
          <button className="btn btn-outline-secondary btn-sm" onClick={openLog}>
            Show email log
          </button>
        </div>

        {/* Breakdown picker + Generate all */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="form-label">Result breakdown</label>
                <select
                  className="form-select"
                  value={breakdownId}
                  onChange={(e) => setBreakdownId(e.target.value)}
                >
                  <option value="">— Select breakdown —</option>
                  {breakdowns.map((b: any) => (
                    <option key={b._id} value={b._id}>
                      {b.title || b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <button
                  className="btn btn-primary"
                  onClick={generateAll}
                  disabled={!breakdownId || generating}
                >
                  {generating ? "Generating…" : "Generate all"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Records */}
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-bordered table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Student</th>
                    <th>GR No</th>
                    <th className="text-center">Version</th>
                    <th>Generated</th>
                    <th>Parent email</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">Loading…</td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        {breakdownId ? "No report cards yet. Pick a breakdown and click Generate all." : "Select a breakdown."}
                      </td>
                    </tr>
                  ) : (
                    records.map((r) => (
                      <tr key={r.id}>
                        <td>{r.studentName}</td>
                        <td>{r.grNumber}</td>
                        <td className="text-center">
                          {r.version}
                          {r.isLatest && <span className="badge bg-primary ms-1">latest</span>}
                        </td>
                        <td>{r.generatedAt ? new Date(r.generatedAt).toLocaleString() : "—"}</td>
                        <td>{r.parentEmail || <span className="text-warning small">none on file</span>}</td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary me-2" onClick={() => download(r.id)}>
                            Download
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            disabled={busyId === r.id}
                            onClick={() => doEmail(r.id)}
                          >
                            {busyId === r.id ? "…" : "Email"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Email log modal */}
        {showLog && (
          <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }} onClick={() => setShowLog(false)}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Email log</h5>
                  <button type="button" className="btn-close" onClick={() => setShowLog(false)} />
                </div>
                <div className="modal-body p-0">
                  <table className="table table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Student</th>
                        <th>Recipient</th>
                        <th>Status</th>
                        <th>When</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {log.length === 0 ? (
                        <tr><td colSpan={5} className="text-center text-muted py-3">No email attempts yet.</td></tr>
                      ) : (
                        log.map((e) => (
                          <tr key={e.id}>
                            <td>{e.studentName}</td>
                            <td className="small">{e.recipientEmail}</td>
                            <td>{statusBadge(e.status)}</td>
                            <td className="small">{new Date(e.sentAt || e.createdAt).toLocaleString()}</td>
                            <td className="small text-muted">{e.errorMessage}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Reports
